/**
 * SmileVerifyCard — Replaces the basic government-ID file upload with a
 * guided Smile ID liveness + ID capture flow. Selfie + ID are captured in
 * a single experience and uploaded as two separate doc_types.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, CheckCircle2, Camera, Loader2,
} from 'lucide-react';
import { SmileCapture } from './SmileCapture';
import { useSmileVerify } from './useSmileVerify';

export const SmileVerifyCard = ({ token, idDone, selfieDone, smileSupported, onUploaded }) => {
  const [open, setOpen] = useState(false);
  const { verifying, submit } = useSmileVerify(token, async () => {
    setOpen(false);
    if (onUploaded) await onUploaded();
  });

  const fullyDone = idDone && selfieDone;

  return (
    <div
      className="border border-emerald-200 rounded-lg p-4 mb-3 bg-emerald-50/40"
      data-testid="succ-doc-smile-verify"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 min-w-0">
          <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              Verify your identity (government ID + selfie)
            </p>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Take a quick selfie and a clear photo of your government-issued ID.
              We use this to confirm you are who you say you are.
            </p>
          </div>
        </div>
        {fullyDone ? (
          <span className="text-xs text-emerald-700 font-medium flex items-center gap-1 whitespace-nowrap">
            <CheckCircle2 className="w-3.5 h-3.5" /> Captured
          </span>
        ) : (
          <span className="text-xs text-slate-500 whitespace-nowrap">Required</span>
        )}
      </div>
      {!fullyDone && (
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() => setOpen(true)}
            disabled={verifying}
            className="bg-emerald-700 hover:bg-emerald-800"
            data-testid="succ-start-smile"
          >
            {verifying ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Uploading</>
            ) : (
              <><Camera className="w-3.5 h-3.5 mr-1" /> Start verification</>
            )}
          </Button>
          {smileSupported && (
            <span
              className="text-[11px] text-slate-500 inline-flex items-center gap-1"
              data-testid="smile-id-trust-badge"
              title="Identity check powered by Smile Identity, a regulated KYC provider."
            >
              <ShieldCheck className="w-3 h-3 text-emerald-700" />
              Verified by Smile&nbsp;ID
            </span>
          )}
        </div>
      )}
      <SmileCapture
        open={open}
        onClose={() => setOpen(false)}
        onCapture={submit}
      />
    </div>
  );
};

export default SmileVerifyCard;
